import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { JmepDiscoverService } from './../../../../service/jmep-discover.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Options } from 'tabulator-tables';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgForm } from '@angular/forms';
import { AutoConfigurationComponent } from './../auto-configuration/auto-configuration.component';
import { DISCOVERY_TYPES, DISCOVERY_MODE } from './../../../datasource.entity.widget/models/types';
import { JmepDiscoverDialogComponent } from '../jmep-discover-dialog/jmep-discover-dialog.component';
import {
  DialogService,
  TabulatorService,
  FusionTransformationService,
  NotificationService,
  UserAuth,
  IPsService
} from '@nrwl/common-lib';

@Component({
  selector: 'nrwl-broadcast-discovery',
  templateUrl: './broadcast-discovery.component.html',
  styleUrls: ['./broadcast-discovery.component.scss'],
  providers: [JmepDiscoverService]
})
export class BroadcastDiscoveryComponent implements OnInit, OnDestroy {
  @Input() selectedEsam: any;
  @ViewChild('currentForm') broadcastDiscoveryForm: NgForm;
  defaultTimeToWait = '5';
  timeToWaitRange = '5-60'; // seconds
  subnetAddressMaskOptions = ['30', '29', '28', '27', '26', '25', '24'];
  subnetAddressMask = '30';
  subnetAddress = '';
  newSubnetaddress = null;
  dataSource: any;
  formData = {
    ipaddress: null,
    subnet: null,
    timeToWait: null,
    autoConfigureOption: true
  };
  finalUpdatedFieldsOptions = [];
  discoveryStates = [];
  discoveredFiles = {};
  discoveryTaskStatus = null;
  discoveryTask = null;
  discoveryResponseStatus = null;
  discoveryResponseMessage = null;
  discoveryResponseError = null;
  showDiscoveyResTable = false;
  showAutoAssignIpBanner = false;
  showDiscoveryResponse = false;
  progressCompleted = 5;
  isAutoConfigEnabled = false;
  autoConfigData = {};
  statusJobId: string = null;
  dataOptions = {
    ipaddress: {
      data: [],
      error: false,
      errorText: [],
      sectionName: 'broadcast-ip-individual'
    },
    subnet: {
      data: [],
      error: false,
      errorText: [],
      sectionName: 'broadcast-ip-subnet'
    },
    timeToWait: {
      data: [],
      error: false,
      errorText: [],
      sectionName: 'discovery-duration-in-sec'
    }
  };
  widgetOptions = {
    defaultSort: {
      id: 'errorMsg',
      type: 'text',
      reverse: true
    },
    pagination: {
      itemsPerPage: 1000
    }
  };
  actions = {};
  searchText = {};
  sendAction = {};
  errorTextLimit = 2; //Limit to show errors
  savingInProgress = false;
  isLoadingProgress = true;
  requestJSONFormat = {
    format: 'json',
    id: 'discovery-config',
    value: {
      data: []
    }
  };
  dialogOptions = {
    preserveScope: true,
    autoWrap: true,
    skipHide: true
  };
  esamId: string = null;
  esamName: string = null;
  esamURL: string = null;
  isCheckBoxEnabled = false;
  discoveryTable: Options;
  discoveryStatusResponseMessage = null;
  readWriteRole = false;
  editConfigLabel = null;
  loopbackInterface = null;
  ARPOptions = null;
  dyingGaspOptions = null;
  timingMode = null;
  fieldsMapping = null;
  isAutoAssignIpEnabled = false;
  savedAutoConfigs = {};
  autoConfigDialogRef: MatDialogRef<any>;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private dialog: MatDialog,
    private jmepDiscoverService: JmepDiscoverService,
    private tabulatorService: TabulatorService,
    private fusionTransformationService: FusionTransformationService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
    private readonly userAuth: UserAuth,
    private readonly ipsService: IPsService,
    public dialogRef: MatDialogRef<JmepDiscoverDialogComponent>
  ) {
    this.readWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
    this.editConfigLabel = this.readWriteRole ? 'Edit configuration...' : 'View configuration...';
    this.discoveryTable = this.tabulatorService.commonTableConfiguration();
    this.discoveryTable.persistenceID = 'jmeps-discovery';
    this.discoveryTable.groupHeader = this.tabulatorService.groupHeader;
  }

  ngOnInit(): void {
    this.esamId = this.selectedEsam?.id;
    this.esamName = this.selectedEsam?.dataItem?.nameId;
    this.esamURL = this.selectedEsam?.dataItem?.connections?.[0]?.uri || '';
    this.loopbackInterface = this.jmepDiscoverService.getLoopbackInterfaceOptions();
    this.ARPOptions = this.jmepDiscoverService.getARPoptions();
    this.dyingGaspOptions = this.jmepDiscoverService.getDyingGaspOptions();
    this.timingMode = this.jmepDiscoverService.getTimingModeOptions();
    this.fieldsMapping = this.getFieldsAttributesMapping();
    this.jmepDiscoverService
      .getJMEPDiscovery({ url: this.esamId, discoverType: DISCOVERY_TYPES.broadcast })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response) {
          this.formData.autoConfigureOption = response?.value?.data?.length > 5;
          this.isLoadingProgress = false;
          this.updateJMEPBroadcastFiles(response);
        }
      });
    this.getAutoConfigurationData();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  //eslint-disable-next-line
  updateJMEPBroadcastFiles(response: any): void {
    if (response?.value?.data?.length) {
      const responseData = response.value.data;
      this.formData.ipaddress = this.jmepDiscoverService.getIPBySection(
        responseData,
        this.dataOptions.ipaddress.sectionName
      );
      this.formData.subnet = this.jmepDiscoverService.getIPBySection(
        responseData,
        this.dataOptions.subnet.sectionName
      );
      this.formData.timeToWait = this.jmepDiscoverService.getTimeToWaitAttributeValue(
        responseData,
        this.dataOptions.timeToWait.sectionName
      );
      this.onIpAddressChange();
      this.setSubnetAddressList();
    } else {
      this.formData.timeToWait = this.defaultTimeToWait;
    }
    this.onTimeToWaitChange();
    this.isLoadingProgress = false;
  }

  clearData(option: string): any {
    this.dataOptions[option].error = false;
    this.dataOptions[option].errorText = [];
    this.dataOptions[option].data = [];
  }

  onIpAddressChange(): void {
    this.clearData('ipaddress');
    const lines = this.jmepDiscoverService.getIpList(this.formData.ipaddress);
    if (lines) {
      for (let address = 0; address < lines.length; address++) {
        this.dataOptions.ipaddress.data.push(lines[address]);
        const isValid = this.ipsService.isIPv4(lines[address]);
        if (!isValid && this.dataOptions.ipaddress.errorText.length < this.errorTextLimit) {
          this.dataOptions.ipaddress.error = true;
          const errorText = lines[address] + ' is not a valid IPV4 address';
          this.dataOptions.ipaddress.errorText.push(errorText);
        } else {
          // MV-12695 - See if address is valid broadcast address
          const addrParts = lines[address].split('.');
          const lastOctet = parseInt(addrParts[addrParts.length - 1], 10);
          if ((lastOctet - 3) % 4 !== 0) {
            this.dataOptions.ipaddress.error = true;
            const errorText = lines[address] + ' is not a valid broadcast address';
            this.dataOptions.ipaddress.errorText.push(errorText);
          }
        }
      }
    }
  }

  getAutoConfigurationData(): void {
    this.jmepDiscoverService
      .getJMEPAutoConfiguration()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response?.config) {
          this.formatAutoConfigData(response.config);
        }
      });
  }

  //eslint-disable-next-line
  formatAutoConfigData(config: any): void {
    this.autoConfigData = {};
    if (config && Object.keys(config)?.length > 0) {
      const configData = config.value.data;
      this.savedAutoConfigs = config;
      this.jmepDiscoverService.setAutoConfigData(config);
      for (let i = 0; i < configData.length; i++) {
        this.autoConfigData[configData[i].name] = configData[i];
      }
    }
  }

  openAutoConfigureDialog(): void {
    this.autoConfigDialogRef = this.dialog.open(AutoConfigurationComponent, {
      width: '700px',
      height: '500px',
      disableClose: false,
      data: {
        savedConfigs: this.savedAutoConfigs
      }
    });
    this.autoConfigDialogRef.afterClosed().subscribe(res => {
      console.log('res', res?.value?.data);
      if (res) {
        this.formatAutoConfigData(res);
      }
    });
  }

  /**
   *  Used to map section and attributes name for each form field.
   *  Maps  subattributes value into form value and vice-versa
   * @return
   *   section - name of the section
   *   attributes - attributes need to consider for each form
   *   dataMapping - maps attributes value to form value and vice-versa
   *    dataValue - attribute value
   *    formValue - form vaue
   */
  getFieldsAttributesMapping(): any {
    return {
      vlan: {
        section: 'network',
        attributes: ['mgmt_vlan'],
        defaultValue: '',
        additionalFieldsMapping: {
          attributes: ['mgmt_vlan_socp_pass_thru'],
          dataValue: ['false']
        }
      },
      selectedARPOption: {
        section: 'network',
        attributes: ['arp_adp_enabled_optical', 'arp_adp_enabled_host'],
        dataMapping: [
          {
            dataValue: ['true', 'false'],
            formValue: this.ARPOptions[1]
          },
          {
            dataValue: ['false', 'true'],
            formValue: this.ARPOptions[2]
          },
          {
            dataValue: ['true', 'true'],
            formValue: this.ARPOptions[3]
          }
        ]
      },
      selectedDyingGaspOption: {
        section: 'otherOptions',
        attributes: [
          'dying_gasp_frp_enabled',
          'dying_gasp_optical_link_oam_enabled',
          'dying_gasp_host_link_oam_enabled'
        ],
        dataMapping: [
          {
            dataValue: ['false', 'false', 'false'],
            formValue: this.dyingGaspOptions[1]
          },
          {
            dataValue: ['true', 'true', 'false'],
            formValue: this.dyingGaspOptions[2]
          },
          {
            dataValue: ['true', 'false', 'true'],
            formValue: this.dyingGaspOptions[3]
          }
        ]
      },
      selectedModeOption: {
        section: 'timing',
        attributes: ['mode', 'host_to_net_timing', 'net_to_host_timing'],
        dataMapping: [
          {
            dataValue: ['asynch_timing', 'disable', 'disable'],
            formValue: this.timingMode[1]
          },
          {
            dataValue: ['through_timing', 'disable', 'disable'],
            formValue: this.timingMode[2]
          }
        ]
      },
      enablePingResponse: {
        section: 'otherOptions',
        attributes: ['ping_ems_ip_pingable', 'ping_customer_visible_ip_pingable'],
        dataMapping: [
          {
            dataValue: ['false', 'false'],
            formValue: { value: 'false' }
          },
          {
            dataValue: ['true', 'true'],
            formValue: { value: 'true' }
          }
        ]
      },
      enableLosPin: {
        section: 'los',
        attributes: ['mask_los_pin'],
        dataMapping: [
          {
            dataValue: ['false'],
            formValue: { value: 'false' }
          },
          {
            dataValue: ['true'],
            formValue: { value: 'true' }
          }
        ],
        additionalFieldsMapping: {
          attributes: ['mask_power_loss', 'mask_pcs_down', 'toggle_pcs_on_los'],
          dataValue: ['false', 'false', 'false']
        },
        defaultValue: false
      },
      equipmentInterface: {
        section: 'loopback',
        attributes: [
          'accept_layer2_port_loopback_request_host',
          'accept_layer3_port_loopback_request_host',
          'accept_layer2_evc_loopback_request_host',
          'accept_layer3_evc_loopback_request_host'
        ],
        dataMapping: [
          {
            dataValue: ['false', 'false', 'false', 'false'],
            formValue: this.loopbackInterface[1]
          },
          {
            dataValue: ['true', 'true', 'false', 'false'],
            formValue: this.loopbackInterface[2]
          },
          {
            dataValue: ['false', 'false', 'true', 'true'],
            formValue: this.loopbackInterface[3]
          }
        ]
      },
      networkInterface: {
        section: 'loopback',
        attributes: [
          'accept_layer2_port_loopback_request_optical',
          'accept_layer3_port_loopback_request_optical',
          'accept_layer2_evc_loopback_request_optical',
          'accept_layer3_evc_loopback_request_optical'
        ],
        dataMapping: [
          {
            dataValue: ['false', 'false', 'false', 'false'],
            formValue: this.loopbackInterface[1]
          },
          {
            dataValue: ['true', 'true', 'false', 'false'],
            formValue: this.loopbackInterface[2]
          },
          {
            dataValue: ['false', 'false', 'true', 'true'],
            formValue: this.loopbackInterface[3]
          }
        ]
      },
      enableViaviHello: {
        section: 'loopback',
        attributes: ['respond_to_hello_request_optical', 'respond_to_hello_request_host'],
        dataMapping: [
          {
            dataValue: ['false', 'false'],
            formValue: { value: 'false' }
          },
          {
            dataValue: ['true', 'true'],
            formValue: { value: 'true' }
          }
        ],
        defaultValue: false
      }
    };
  }

  setSubnetAddressList(): void {
    const lines = this.jmepDiscoverService.getIpList(this.formData.subnet);
    if (lines) {
      for (let address = 0; address < lines.length; address++) {
        this.dataOptions.subnet.data.push(lines[address]);
      }
    }
  }

  addNewSubnetaAddress(): void {
    if (this.newSubnetaddress) {
      this.dataOptions.subnet.data.unshift(this.newSubnetaddress);
      this.newSubnetaddress = null;
      this.updateTransmitDuration();
    }
  }

  removeSubnetaAddress(arrayIndex: number): void {
    this.dataOptions.subnet.data.splice(arrayIndex, 1);
    this.updateTransmitDuration();
  }

  onSubnetChange(): void {
    let isValid = false;
    let errorText = null;
    if (this.subnetAddress && this.subnetAddressMask) {
      const subnetString = this.subnetAddress + '.0/' + this.subnetAddressMask;
      if (subnetString) {
        isValid = this.ipsService.isIPv4Subnet(subnetString);
        if (isValid) {
          if (this.dataOptions.subnet.data.indexOf(subnetString) === -1) {
            this.newSubnetaddress = subnetString;
          } else {
            isValid = false;
            errorText = 'Address already exist';
          }
        } else {
          errorText = 'Invalid address';
        }
      }
      this.dataOptions.subnet.error = !isValid;
      this.dataOptions.subnet.errorText = [];
      if (errorText) {
        this.dataOptions.subnet.errorText.push(errorText);
      }
    } else {
      this.dataOptions.subnet.error = true;
      this.dataOptions.subnet.errorText = [];
    }
  }

  updateTransmitDuration(): void {
    let numOfIPs = 0;
    let transmitDuration = parseInt(this.defaultTimeToWait);
    const subnetList = this.dataOptions.subnet.data;
    const ipAddresses = this.dataOptions.ipaddress.data;
    if (this.dataOptions.ipaddress.errorText.length === 0 && ipAddresses.length > 0) {
      numOfIPs = ipAddresses.length;
    }
    if (subnetList.length > 0) {
      for (let i = 0; i < subnetList.length; i++) {
        const splitSubnet = subnetList[i].split('/');
        const ipsCountsArr = this.jmepDiscoverService.subnetIPscountObj.filter(function (o) {
          return o.subnet === splitSubnet[1];
        });
        if (ipsCountsArr.length > 0) {
          numOfIPs = numOfIPs + ipsCountsArr[0].broadCastCount;
        }
      }
    }
    if (numOfIPs) {
      transmitDuration = this.jmepDiscoverService.getTransmitDuration(numOfIPs);
    }
    this.formData.timeToWait = transmitDuration;
    this.clearData('timeToWait');
    this.dataOptions.timeToWait.data.push(transmitDuration);
  }

  onTimeToWaitChange(): void {
    this.clearData('timeToWait');
    const time = this.formData.timeToWait;
    this.dataOptions.timeToWait.data.push(time);
    const errMsg = this.jmepDiscoverService.validateTimeRange(
      this.formData.timeToWait,
      this.timeToWaitRange,
      false
    );
    if (errMsg) {
      this.dataOptions.timeToWait.error = true;
      this.dataOptions.timeToWait.errorText.push(errMsg);
    }
  }

  isValidAddress(): boolean {
    return (
      this.dataOptions.ipaddress.error ||
      this.dataOptions.subnet.error ||
      this.dataOptions.timeToWait.error ||
      (this.formData?.ipaddress === '' && this.dataOptions?.subnet?.data?.length === 0)
    );
  }

  clearExistingDiscoveryResponse(): void {
    this.discoveryResponseStatus = null;
    this.discoveryResponseMessage = null;
    this.discoveryResponseError = null;
    this.showDiscoveyResTable = false;
    this.isAutoConfigEnabled = false;
    this.finalUpdatedFieldsOptions = [];
    this.dataSource = [];
    this.discoveryStates = [];
    this.jmepDiscoverService.setAutoAssignIPEnabledStatus(this.isAutoConfigEnabled);
  }

  refreshConfiguration(): void {
    const configData = this.jmepDiscoverService.getAutoConfigData();
    this.autoConfigData = {};
    if (Object.keys(configData).length > 0) {
      for (let i = 0; i < configData.value.data.length; i++) {
        if (
          !this.isAutoConfigEnabled &&
          this.formData.autoConfigureOption &&
          configData.value.data[i].value !== 'Use existing value'
        ) {
          this.isAutoConfigEnabled = true;
        }
        this.autoConfigData[configData.value.data[i].name] = configData.value.data[i];
      }
    }
  }

  // eslint-disable-next-line
  autoConfigToggleChange($event: any): void {
    this.formData.autoConfigureOption = $event.checked;
  }

  //eslint-disable-next-line
  createJMEPDiscoveryData(section: string, fieldValues: any, isSubAttributes: boolean): void {
    const sectionIndex = this.finalUpdatedFieldsOptions.findIndex(field => field.name === section);
    if (isSubAttributes) {
      const subAttributesArray = [];
      fieldValues.forEach(value => {
        subAttributesArray.push({
          id: 'ip',
          value: value
        });
      });
      if (sectionIndex >= 0) {
        this.finalUpdatedFieldsOptions[sectionIndex].subAttributes =
          this.finalUpdatedFieldsOptions[sectionIndex].subAttributes.concat(subAttributesArray);
      } else {
        this.finalUpdatedFieldsOptions.push({ name: section, subAttributes: subAttributesArray });
      }
    } else {
      if (sectionIndex >= 0) {
        this.finalUpdatedFieldsOptions[sectionIndex].value = fieldValues;
      } else {
        this.finalUpdatedFieldsOptions.push({ name: section, value: fieldValues });
      }
    }
  }

  saveDiscoverDetails(): void {
    this.clearExistingDiscoveryResponse();
    this.refreshConfiguration();
    this.createJMEPDiscoveryData(
      this.dataOptions.ipaddress.sectionName,
      this.dataOptions.ipaddress.data,
      true
    );
    this.createJMEPDiscoveryData(
      this.dataOptions.subnet.sectionName,
      this.dataOptions.subnet.data,
      true
    );
    this.createJMEPDiscoveryData(
      this.dataOptions.timeToWait.sectionName,
      this.dataOptions.timeToWait.data[0],
      false
    );
    this.createJMEPDiscoveryData('esam-name', this.esamName, false);
    this.createJMEPDiscoveryData('discovery-mode', DISCOVERY_MODE.boradcast, false);
    if (this.formData.autoConfigureOption) {
      this.jmepDiscoverService.setAutoAssignIPEnabledStatus(this.isAutoConfigEnabled);
      if (this.autoConfigData?.['ipAddress']) {
        this.isAutoAssignIpEnabled = this.autoConfigData?.['ipAddress']?.value === 'Auto-assign';
      }
      this.createJMEPDiscoveryData(
        'auto-assign-probe-ip',
        this.isAutoAssignIpEnabled ? 'true' : 'false',
        false
      );
      this.getUpdatedFieldAttributes(this.autoConfigData);
    }
    this.requestJSONFormat.value.data = this.finalUpdatedFieldsOptions;
    this.onSetJMEPDiscovery('startDiscovery');
  }

  hasDataOptions(): number {
    return this.dataOptions.ipaddress.data.length || this.dataOptions.subnet.data.length;
  }

  onClearAllAddresses(): void {
    const params = {
      id: 'discovery.clear_all_addresses',
      callBackService: this,
      callBackFuntion: this.clearAllAddresses,
      callBackFnParams: {
        self: this
      }
    };
    this.dialogService.showDialog('confirm', 'body', params);
  }

  //eslint-disable-next-line
  clearAllAddresses(thisRef: any): void {
    let isAutoAssignIpEnabled = false;
    thisRef.refreshConfiguration();
    thisRef.formData.ipaddress = [];
    thisRef.formData.subnet = [];
    thisRef.subnetAddress = '';
    thisRef.clearData('ipaddress');
    thisRef.clearData('subnet');
    thisRef.createJMEPDiscoveryData(
      thisRef.dataOptions.ipaddress.sectionName,
      thisRef.dataOptions.ipaddress.data,
      true
    );
    thisRef.createJMEPDiscoveryData(
      thisRef.dataOptions.subnet.sectionName,
      thisRef.dataOptions.subnet.data,
      true
    );
    thisRef.createJMEPDiscoveryData(
      thisRef.dataOptions.timeToWait.sectionName,
      thisRef.dataOptions.timeToWait.data[0],
      false
    );
    thisRef.createJMEPDiscoveryData('esam-name', thisRef.esamName, false);
    thisRef.createJMEPDiscoveryData('discovery-mode', DISCOVERY_MODE.boradcast, false);
    if (thisRef.formData.autoConfigureOption) {
      thisRef.jmepDiscoverService.setAutoAssignIPEnabledStatus(thisRef.isAutoConfigEnabled);
      if (this.autoConfigData?.['ipAddress']) {
        isAutoAssignIpEnabled = this.autoConfigData?.['ipAddress'] === 'Auto-assign';
      }
      thisRef.createJMEPDiscoveryData(
        'auto-assign-probe-ip',
        isAutoAssignIpEnabled ? 'true' : 'false',
        false
      );
      thisRef.getUpdatedFieldAttributes(thisRef.autoConfigData);
    }
    thisRef.requestJSONFormat.value.data = thisRef.finalUpdatedFieldsOptions;
    if (!thisRef.hasDataOptions()) {
      thisRef.onSetJMEPDiscovery('clearAllAddresses');
    }
  }

  showDiscoverSetup(): void {
    this.showDiscoveryResponse = false;
  }

  updateProgressCompletion(): void {
    const duration = 180000; // 180  sec
    const percent = duration / 100;
    let i = 0;
    const interval = setInterval(function () {
      i++;
      this.progressCompleted = i;
      if (i >= 100) {
        clearInterval(interval);
      }
    }, percent);
  }

  //eslint-disable-next-line
  getUpdatedFieldAttributes(updatedFields: any): void {
    let fieldValue = null;
    let fieldValues = null;
    for (const key of Object.keys(updatedFields)) {
      const fieldAttributes = this.fieldsMapping[key];
      if (fieldAttributes) {
        const additionalFieldNames = fieldAttributes?.additionalFieldsMapping;
        switch (key) {
          case 'vlan':
            fieldValue = updatedFields[key]?.value ? updatedFields[key].value : '0';
            additionalFieldNames.attributes.push('mgmt_vlan_disabled');
            additionalFieldNames.dataValue.push(updatedFields[key]?.value ? 'false' : 'true');
            this.createProbeAttributes(
              fieldAttributes.section,
              fieldAttributes.attributes,
              [fieldValue],
              additionalFieldNames
            );
            break;
          case 'selectedModeOption':
          case 'selectedARPOption':
          case 'selectedDyingGaspOption':
          case 'enablePingResponse':
          case 'enableLosPin':
          case 'equipmentInterface':
          case 'networkInterface':
          case 'enableViaviHello':
            fieldValues = fieldAttributes.dataMapping.find(
              data => data?.formValue?.value === updatedFields[key]?.value
            );
            if (fieldValues) {
              this.createProbeAttributes(
                fieldAttributes.section,
                fieldAttributes.attributes,
                fieldValues?.dataValue,
                fieldAttributes.additionalFieldsMapping
              );
            }
            break;
        }
      }
    }
  }

  //eslint-disable-next-line
  createProbeAttributes(
    section: string,
    fieldNames: any,
    fieldValues: any,
    additionalFieldNames: any
  ): void {
    const subAttributesArray = [];
    fieldNames.forEach((fieldName, index) => {
      subAttributesArray.push({
        id: fieldName,
        value: fieldValues[index]
      });
    });
    if (additionalFieldNames) {
      additionalFieldNames.attributes.forEach((fieldName, index) => {
        subAttributesArray.push({
          id: fieldName,
          value: additionalFieldNames.dataValue[index]
        });
      });
    }
    const sectionIndex = this.finalUpdatedFieldsOptions.findIndex(field => field.name === section);
    if (sectionIndex >= 0) {
      this.finalUpdatedFieldsOptions[sectionIndex].subAttributes =
        this.finalUpdatedFieldsOptions[sectionIndex].subAttributes.concat(subAttributesArray);
    } else {
      this.finalUpdatedFieldsOptions.push({ name: section, subAttributes: subAttributesArray });
    }
  }

  //eslint-disable-next-line
  onSetJMEPDiscovery(eventType: any): void {
    this.savingInProgress = eventType === 'startDiscovery';
    this.discoveryStatusResponseMessage = null;
    this.jmepDiscoverService
      .setJMEPDiscovery({
        url: this.esamURL,
        esamId: this.esamId,
        option: this.requestJSONFormat,
        discoverType: DISCOVERY_TYPES.broadcast,
        eventType: eventType
      })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.setDiscoverComplete(response);
        },
        errorRes => {
          this.discoveryResponseError = errorRes?.error?.data?.message || '';
          this.notificationService.onFail(
            'Failed to JMEP discovery: ' + this.discoveryResponseError
          );
          this.savingInProgress = false;
        }
      );
  }

  //eslint-disable-next-line
  discoveryStatusMessage(response: any): void {
    if (response?.data?.['general-info']?.status?.jobStatus) {
      this.discoveryTaskStatus = response.data['general-info'].status.jobStatus;
      if (this.discoveryTaskStatus === 'FINISHED') {
        this.showDiscoveryResponse = true;
        if (response.data['general-info'].status.message.indexOf('No JMEPs found') != -1) {
          this.savingInProgress = false;
          this.discoveryResponseMessage = response.data['general-info'].status.message;
        } else {
          this.jmepDiscoverService
            .getDiscoveryResult(this.statusJobId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(res => {
              if (res) {
                this.discoveryResults(res);
              }
            });
        }
      } else {
        this.discoveryStatusResponseMessage = response.data['general-info'].status.message;
      }
    }
  }

  //eslint-disable-next-line
  discoveryResults(response: any): void {
    if (
      response &&
      response.data &&
      response.data['general-info'] &&
      response.data['general-info']['discovery-results']
    ) {
      this.discoveryResponseMessage = response.data['general-info']['discovery-results'].message;
      if (response.data['general-info']['discovery-results']['results']) {
        this.showDiscoveyResTable = true;
        this.assignDiscoveryResultTable(
          response.data['general-info']['discovery-results']['results']
        );
      }
    }
  }

  //eslint-disable-next-line
  assignDiscoveryResultTable(res: any): void {
    this.jmepDiscoverService.clearResultCounts();
    const tableObj = res.map(item =>
      this.jmepDiscoverService.getTableObject(item, DISCOVERY_TYPES.broadcast)
    );
    this.dataSource = this.fusionTransformationService.transformData('jmeps-discovery', tableObj);
    const activeColumns = Object.keys(tableObj?.[0]);
    this.discoveryStates = this.jmepDiscoverService.getDiscoveryStatesCount(
      res.length,
      DISCOVERY_TYPES.broadcast
    );
    if (tableObj?.length) {
      if (!this.isAutoAssignIpEnabled) {
        // Remove Auto configured status, If not enabled.
        const autoConfigIndex = this.discoveryStates.findIndex(
          field => field.title === 'Auto configured'
        );
        if (autoConfigIndex !== -1) {
          this.discoveryStates.splice(autoConfigIndex, 1);
        }

        const index = activeColumns.findIndex(field => field === 'ipAssignStatus');
        if (index !== -1) {
          activeColumns.splice(index, 1);
        }
      }
      this.dataSource.column = this.dataSource.column.map(el => {
        if (activeColumns.find(field => el.tabulatorInfo.field === field && field !== 'id')) {
          el.tabulatorInfo.visible = true;
        } else {
          el.tabulatorInfo.visible = false;
        }
        return el;
      });
    }
    this.discoveryTable.columns = this.dataSource.column;
    this.discoveryTable.data = this.dataSource.data;
    this.discoveryTable.minHeight = 250;
    this.discoveryTable.height = 400;
    if (this.discoveryTable.persistence) {
      this.discoveryTable.persistence.sort = true;
      this.discoveryTable.persistence.page = true;
      this.discoveryTable.initialSort = [{ column: 'ipAddress', dir: 'asc' }];
    }
    this.savingInProgress = false;
    this.discoveryTable = Object.assign({}, this.discoveryTable);
  }

  //eslint-disable-next-line
  setDiscoverComplete(response: any): void {
    this.discoveryTaskStatus = 'IN_PROGRESS';
    this.discoveryTask = response.status;
    this.discoveryResponseStatus = response.status;
    if (response.status === 'success') {
      this.statusJobId = response.data['general-info'].status.jobId;
      setInterval(() => {
        if (this.discoveryTaskStatus !== 'FINISHED' && this.discoveryTask === 'success') {
          this.jmepDiscoverService
            .discoveryStatusMessage(this.statusJobId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(res => {
              if (res) {
                this.discoveryStatusMessage(res);
              }
            });
        }
      }, 5000);
    }
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
